// @flow
import { TRACKING_EVENTS } from './events';

type User = {
  id: string,
  grade?: { name: string },
  rule?: string,
  bizNumber?: string,
  [key: string]: any,
};

type PageInfo = {
  path: string,
  title: string,
};

const getPageInfo = (location?: { pathname: string, search: string }): PageInfo => {
  if (typeof window === 'undefined') {
    return { path: '', title: '' };
  }
  const path = location
    ? location.pathname + location.search
    : window.location.pathname + window.location.search;
  return {
    path,
    title: document.title || '',
  };
};

export const setUser = (user: User): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push({
    user_id: user.id,
    user_properties: {
      grade: user.grade && user.grade.name ? user.grade.name : '',
      rule: user.rule || '',
      biz_number: user.bizNumber || '',
    },
  });
};

export const pushPageView = (location?: { pathname: string, search: string }): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  const page = getPageInfo(location);
  window.dataLayer.push({
    event: 'pageview',
    page: {
      path: page.path,
      search: window.location.search || '',
      title: page.title,
    },
  });
};

const mapItemsToGA4 = (items: any[]): any[] => {
  return items.map((item, index) => ({
    item_id: item.productId || item.id || '',
    item_name: item.productTitle || item.title || '',
    item_brand: item.brand || '',
    price: item.price || 0,
    quantity: item.quantity || 1,
    index,
  }));
};

const mapToGA4Event = (
  eventName: string,
  properties: { [key: string]: any },
): ?{ event: string, ecommerce?: Object } => {
  switch (eventName) {
    case TRACKING_EVENTS.PRODUCT_VIEWED:
      return {
        event: 'view_item',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
            },
          ],
        },
      };
    case TRACKING_EVENTS.PRODUCT_LIST_VIEWED:
      return {
        event: 'view_item_list',
        ecommerce: {
          item_list_id: properties.listId || '',
          item_list_name: properties.listName || '',
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PRODUCT_CART_ADDED:
      return {
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
              quantity: properties.quantity || 1,
            },
          ],
        },
      };
    case TRACKING_EVENTS.PRODUCT_CART_REMOVED:
      return {
        event: 'remove_from_cart',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              quantity: properties.quantity || 1,
            },
          ],
        },
      };
    case TRACKING_EVENTS.CART_VIEWED:
      return {
        event: 'view_cart',
        ecommerce: {
          currency: 'KRW',
          value: properties.value || 0,
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.CHECKOUT_STARTED:
      return {
        event: 'begin_checkout',
        ecommerce: {
          currency: 'KRW',
          value: properties.value || 0,
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PURCHASE_COMPLETED:
      return {
        event: 'purchase',
        ecommerce: {
          transaction_id: properties.transactionId || '',
          currency: 'KRW',
          value: properties.value || 0,
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PRODUCT_WISHLIST_ADDED:
      return {
        event: 'add_to_wishlist',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
            },
          ],
        },
      };
    default:
      return null;
  }
};

export const push = (eventName: string, properties: { [key: string]: any } = {}): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;

  const ga4Event = mapToGA4Event(eventName, properties);
  if (ga4Event) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(ga4Event);
  }
};



// WEBPACK FOOTER //
// ./src/lib/tracking/gtmTracker.js